/**
 * 📅 Booking Entity (الحجز)
 *
 * Aggregate Root يربط:
 *   - venueId (القاعة المحجوزة)
 *   - customerId (العميل)
 *   - bookingDate (يوم الحجز)
 *   - guests (عدد الضيوف)
 *   - status (الحالة الحالية)
 *
 * ملاحظة: نموذج lead-gen + اشتراكات سنوية — الحجز لا يحمل أي مبلغ/عمولة.
 *
 * Business Rules:
 *   - يوم الحجز لازم في المستقبل
 *   - عدد الضيوف ضمن capacity القاعة
 *   - status transitions تتبع State Machine
 */

import {
  BookingRequestStatus,
  type BookingRequestStatusType,
  canTransitionBooking,
} from '@domain/value-objects/BookingRequestStatus';
import {
  ValidationError,
  BusinessRuleViolationError,
} from '@domain/errors/DomainErrors';
import type {
  DayTypeValue,
  SeasonValue,
  SectionModeValue,
} from '@domain/value-objects/PackageEnums';

export type CustomerGender = 'male' | 'female' | 'other';

export interface CustomerInfo {
  /**
   * 🔑 uid حساب التطبيق — له قيمة فقط لعملاء تطبيق Havlaa المسجّلين.
   *    null لطلبات الدشبورد (تليفوني/حضوري — لا حساب في التطبيق).
   *    التمييز بين عميل التطبيق والدشبورد يعتمد على `source` + وجود uid.
   */
  uid: string | null;
  name: string;
  phone: string;
  email?: string | undefined;
  gender?: CustomerGender | undefined;

  /**
   * مرجع لعميل التطبيق في customers/ (لعملاء التطبيق المسجّلين)
   */
  appCustomerId?: string | undefined;

  /**
   * مرجع لعميل المزود في providers/{providerId}/customers/
   */
  providerCustomerId?: string | undefined;
}

/**
 * 📡 مصدر إنشاء الحجز
 *   Legacy:
 *     - app: من تطبيق العميل (Flutter)
 *     - dashboard: التاجر/الأدمن أنشأ يدوياً
 *     - web: من الموقع (مستقبلاً)
 *     - seed: بيانات تجريبية
 *
 *   🆕 Phase 2 — قيم أوضح (تستخدم تدريجياً):
 *     - havlaa_app: من تطبيق Havlaa (يحل محل 'app')
 *     - provider_dashboard: لوحة المزود (يحل محل 'dashboard')
 *     - admin_dashboard: لوحة الأدمن
 *     - phone_call: حجز عبر مكالمة
 *     - whatsapp: حجز عبر واتساب
 *     - walk_in: زيارة شخصية
 */
export type BookingSource =
  // legacy
  | 'app'
  | 'dashboard'
  | 'web'
  | 'seed'
  // Phase 2
  | 'havlaa_app'
  | 'provider_dashboard'
  | 'admin_dashboard'
  | 'phone_call'
  | 'whatsapp'
  | 'walk_in';

/**
 * 🔍 لقطة فلاتر البحث وقت الحجز (للتحليلات)
 */
export interface BookingSearchSnapshot {
  city?: string | undefined;
  budget?: number | undefined;
  guests?: number | undefined;
  sectionMode?: string | undefined;  // 'both' | 'men' | 'women' | 'any'
  dayType?: string | undefined;
  season?: string | undefined;
  services?: string | undefined;     // 'yes' | 'no' | 'any'
}

/**
 * 📦 معلومات البكج المحجوز (snapshot وقت الحجز)
 */
export interface BookingPackageInfo {
  key: string;                  // packageKey
  name?: string;                // package marketing title (legacy bookings may omit it)
  sectionMode: SectionModeValue;
  dayType: DayTypeValue;
  season: SeasonValue;
  hasIncludedServices: boolean;
  includedServicesText?: string;
  includedServices?: import('@domain/value-objects/IncludedServices').IncludedServices;
  includedServicesMenText?: string;
  includedServicesMen?: import('@domain/value-objects/IncludedServices').MenIncludedServices;
  includedServicesWomenText?: string;
  includedServicesWomen?: import('@domain/value-objects/IncludedServices').WomenIncludedServices;
}

export interface BookingProps {
  id: string;
  /** 🆔 displayId للعرض (HVL-2605-AB1C23) */
  displayId?: string | undefined;
  venueId: string;
  venueName: string;        // denormalized للسرعة
  providerId: string;       // مرجع المزوّد القياسي (organization)
  /** Provider user currently responsible for customer contact on this request. */
  assignedProviderUserId?: string;
  /** Auth UID that performed the contacted transition (audit field). */
  contactedBy?: string;
  customer: CustomerInfo;
  bookingDate: string;       // YYYY-MM-DD
  /**
   * 🕐 الفترة — 'evening' افتراضياً. مُعدّ للمستقبل (حجز صباحي/مسائي).
   *    يدخل في slot doc id لـ booking_dates: `${eventDate}_${timeSlot}`.
   */
  timeSlot?: string;
  guestCount: number;
  /** Canonical guest composition; omitted only on legacy bookings. */
  guestType?: SectionModeValue;
  menGuestCount?: number;
  womenGuestCount?: number;
  status: BookingRequestStatusType;
  packageInfo?: BookingPackageInfo;  // 📦 البكج المحجوز (snapshot — legacy)
  /** 📸 لقطة القاعة الكاملة (الحقل `venue` في Firestore — بصيغة Flutter Venue.toMap) */
  venueSnapshot?: Record<string, unknown> | undefined;
  /** 📦 لقطة الباقة الكاملة (الحقل `package` في Firestore — بصيغة Flutter VenuePackage.toMap) */
  packageSnapshot?: Record<string, unknown> | undefined;
  notes?: string;            // ملاحظات العميل
  vendorNotes?: string;      // ملاحظات التاجر (سبب الرفض مثلاً)
  /** 📡 مصدر الحجز */
  source?: BookingSource | undefined;
  /** 🔍 فلاتر البحث وقت الحجز (للتحليلات) */
  searchSnapshot?: BookingSearchSnapshot | undefined;
  createdAt: Date;
  updatedAt: Date;
  /** متى تواصل المزود مع العميل (status = contacted) */
  contactedAt?: Date;
  /** متى تم تأكيد الحجز خارج التطبيق (status = completed_externally) */
  completedExternallyAt?: Date;
  rejectedAt?: Date;
  cancelledAt?: Date;
  /** متى أُغلق الطلب لأن التاريخ حُجز لطلب آخر (status = booked_elsewhere) */
  bookedElsewhereAt?: Date;
  /** متى انتهت صلاحية الطلب (status = expired) */
  expiredAt?: Date;
}

export class Booking {
  private constructor(public readonly props: BookingProps) {}

  // ─── Factory ───
  static create(props: BookingProps): Booking {
    Booking.validate(props);
    return new Booking(props);
  }

  private static validate(props: BookingProps): void {
    // 1️⃣ Date format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(props.bookingDate)) {
      throw new ValidationError('تنسيق التاريخ يجب أن يكون YYYY-MM-DD', {
        field: 'bookingDate',
      });
    }

    // 🛡️ (H-6 fix) Date sanity — يمنع تواريخ غير منطقية
    //   كان regex فقط، يقبل '2024-02-31' أو تواريخ ماضية.
    //   الآن نتحقق:
    //     - الـ date فعلاً صالح (Date constructor)
    //     - مش ماضي (إلا لو الحجز موجود مسبقاً — للـ migration)
    //     - مش أبعد من سنتين (sanity check)
    const dateObj = new Date(props.bookingDate + 'T00:00:00');
    if (isNaN(dateObj.getTime())) {
      throw new ValidationError('التاريخ غير صالح', {
        field: 'bookingDate',
      });
    }

    // تحقق إن الـ date يطابق الـ string الأصلي (sanity check)
    // (يمنع 2024-02-31 → JS بيحوله لـ 2024-03-02)
    //
    // 🛡️ Fix: نستخدم local components (getFullYear/getMonth/getDate)
    //   بدلاً من toISOString() اللي يحوّل لـ UTC.
    //   في timezone UTC+3 (السعودية)، toISOString() يرجع يوم سابق.
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const reconstructed = `${year}-${month}-${day}`;
    if (reconstructed !== props.bookingDate) {
      throw new ValidationError('التاريخ غير منطقي (يوم/شهر غير صحيح)', {
        field: 'bookingDate',
      });
    }

    // 🛡️ منع التواريخ الماضية (إلا لو الـ booking موجود سابقاً)
    //   نسمح بـ 1 يوم في الماضي للـ timezone tolerance
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const oneDayAgo = new Date(today);
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);

    const isNewBooking = !props.createdAt || props.createdAt > oneDayAgo;
    if (isNewBooking && dateObj < oneDayAgo) {
      throw new ValidationError('لا يمكن إنشاء حجز في تاريخ ماضي', {
        field: 'bookingDate',
      });
    }

    // 🛡️ منع التواريخ البعيدة جداً (sanity — أكثر من سنتين)
    const maxFutureDate = new Date(today);
    maxFutureDate.setFullYear(maxFutureDate.getFullYear() + 2);
    if (dateObj > maxFutureDate) {
      throw new ValidationError('التاريخ أبعد من سنتين — تحقق من الإدخال', {
        field: 'bookingDate',
      });
    }

    // 2️⃣ Guest count
    if (props.guestCount < 1) {
      throw new ValidationError('عدد الضيوف يجب أن يكون 1 على الأقل', {
        field: 'guestCount',
      });
    }

    Booking.validateGuestComposition(props);

    // 4️⃣ Customer info — الاسم مطلوب؛ uid اختياري (null لطلبات الدشبورد)
    if (!props.customer.name) {
      throw new ValidationError('اسم العميل ناقص', {
        field: 'customer',
      });
    }
  }

  private static validateGuestComposition(props: BookingProps): void {
    const supplied = [
      props.guestType,
      props.menGuestCount,
      props.womenGuestCount,
    ].filter((value) => value !== undefined).length;

    // Legacy bookings may omit the complete composition snapshot.
    if (supplied === 0) return;
    if (supplied !== 3) {
      throw new ValidationError('بيانات توزيع الضيوف يجب أن تُحفظ كاملة', {
        field: 'guestType',
      });
    }

    const men = props.menGuestCount!;
    const women = props.womenGuestCount!;
    if (
      !Number.isInteger(men) ||
      !Number.isInteger(women) ||
      men < 0 ||
      women < 0 ||
      men + women !== props.guestCount
    ) {
      throw new ValidationError('توزيع الرجال والنساء لا يطابق إجمالي الضيوف', {
        field: 'guestCount',
      });
    }

    const consistent =
      (props.guestType === 'men' && men === props.guestCount && women === 0) ||
      (props.guestType === 'women' && men === 0 && women === props.guestCount) ||
      (props.guestType === 'both' && men > 0 && women > 0);

    if (!consistent) {
      throw new ValidationError('نوع القسم لا يطابق توزيع الضيوف', {
        field: 'guestType',
      });
    }
  }

  // ─── Getters ───
  get id() { return this.props.id; }
  get venueId() { return this.props.venueId; }
  get providerId(): string | undefined { return this.props.providerId; }
  get hasProvider(): boolean { return !!this.props.providerId; }
  get status() { return this.props.status; }
  get bookingDate() { return new Date(this.props.bookingDate + 'T00:00:00'); }

  /**
   * 🛡️ (L-5 fix) Timezone-aware date comparison
   *
   * كان `new Date().setHours(0,0,0,0)` يستخدم timezone المحلي للجهاز.
   * في جهاز عميل بدول مختلفة، النتيجة تختلف.
   * الآن: نقارن بالـ string format (YYYY-MM-DD) في Asia/Riyadh.
   */
  get isPast(): boolean {
    const todayRiyadh = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Riyadh',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
    // 'en-CA' format = YYYY-MM-DD (نفس الـ bookingDate format)
    return this.props.bookingDate < todayRiyadh;
  }

  get isUpcoming(): boolean {
    return !this.isPast;
  }

  get daysUntilBooking(): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = this.bookingDate;
    return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  }

  // ─── Business Methods ───

  /**
   * 📞 المزود تواصل مع العميل
   *
   * ⚠️ لا يحجز التاريخ — مجرد تحديث حالة الطلب (lead).
   *    الحجز الفعلي يتم عبر إجراء "تم حجز التاريخ" المنفصل.
   */
  markContacted(): Booking {
    if (!canTransitionBooking(this.props.status, BookingRequestStatus.CONTACTED)) {
      throw new BusinessRuleViolationError(
        `لا يمكن تحديث الطلب إلى "تم التواصل" من حالة ${this.props.status}`,
        { rule: 'INVALID_TRANSITION' }
      );
    }

    return new Booking({
      ...this.props,
      status: BookingRequestStatus.CONTACTED,
      contactedAt: new Date(),
      updatedAt: new Date(),
    });
  }

  /**
   * 🎉 تم الحجز — أكّد المزود أن الاتفاق/الحجز تمّ خارج التطبيق (نهائية)
   *
   * ⚠️ هذه حالة الطلب (lead). تثبيت توفّر التاريخ نفسه (booked) إجراء منفصل.
   */
  markCompletedExternally(): Booking {
    if (
      !canTransitionBooking(
        this.props.status,
        BookingRequestStatus.COMPLETED_EXTERNALLY
      )
    ) {
      throw new BusinessRuleViolationError(
        `لا يمكن تحديث الطلب إلى "تم الحجز" من حالة ${this.props.status}`,
        { rule: 'INVALID_TRANSITION' }
      );
    }

    return new Booking({
      ...this.props,
      status: BookingRequestStatus.COMPLETED_EXTERNALLY,
      completedExternallyAt: new Date(),
      updatedAt: new Date(),
    });
  }

  /**
   * 📅 أُغلق الطلب لأن التاريخ حُجز لطلب آخر (تلقائي)
   */
  markBookedElsewhere(): Booking {
    if (!canTransitionBooking(this.props.status, BookingRequestStatus.BOOKED_ELSEWHERE)) {
      throw new BusinessRuleViolationError(
        `لا يمكن إغلاق طلب بحالة ${this.props.status}`,
        { rule: 'INVALID_TRANSITION' }
      );
    }

    return new Booking({
      ...this.props,
      status: BookingRequestStatus.BOOKED_ELSEWHERE,
      bookedElsewhereAt: new Date(),
      updatedAt: new Date(),
    });
  }

  /**
   * ⌛ انتهت صلاحية الطلب (تلقائي — مهلة أو مرور التاريخ)
   */
  expire(): Booking {
    if (!canTransitionBooking(this.props.status, BookingRequestStatus.EXPIRED)) {
      throw new BusinessRuleViolationError(
        `لا يمكن إنهاء صلاحية طلب بحالة ${this.props.status}`,
        { rule: 'INVALID_TRANSITION' }
      );
    }

    return new Booking({
      ...this.props,
      status: BookingRequestStatus.EXPIRED,
      expiredAt: new Date(),
      updatedAt: new Date(),
    });
  }

  /**
   * ❌ المزود يرفض الطلب
   */
  reject(reason: string): Booking {
    if (!canTransitionBooking(this.props.status, BookingRequestStatus.REJECTED)) {
      throw new BusinessRuleViolationError(
        `لا يمكن رفض حجز بحالة ${this.props.status}`,
        { rule: 'INVALID_TRANSITION' }
      );
    }

    if (!reason.trim()) {
      throw new ValidationError('يجب توضيح سبب الرفض', { field: 'reason' });
    }

    return new Booking({
      ...this.props,
      status: BookingRequestStatus.REJECTED,
      vendorNotes: reason.trim(),
      rejectedAt: new Date(),
      updatedAt: new Date(),
    });
  }

  /**
   * 🚫 إلغاء الحجز
   */
  cancel(reason?: string): Booking {
    if (!canTransitionBooking(this.props.status, BookingRequestStatus.CANCELLED)) {
      throw new BusinessRuleViolationError(
        `لا يمكن إلغاء حجز بحالة ${this.props.status}`,
        { rule: 'INVALID_TRANSITION' }
      );
    }

    const next: BookingProps = {
      ...this.props,
      status: BookingRequestStatus.CANCELLED,
      cancelledAt: new Date(),
      updatedAt: new Date(),
    };

    const trimmed = reason?.trim();
    if (trimmed) {
      next.vendorNotes = trimmed;
    }

    return new Booking(next);
  }

  // ─── Serialization ───
  toFirestore() {
    return {
      code: this.props.displayId ?? null,  // HVL-...
      venueId: this.props.venueId,
      venueName: this.props.venueName,
      // providerId هو مرجع الملكية الوحيد المخزن.
      providerId: this.props.providerId,
      ...(this.props.assignedProviderUserId
        ? { assignedProviderUserId: this.props.assignedProviderUserId }
        : {}),
      ...(this.props.contactedBy ? { contactedBy: this.props.contactedBy } : {}),
      customer: this.props.customer,
      // alias للتوافق مع Flutter/CF — null لطلبات الدشبورد (عميل غير مسجّل)
      userId: this.props.customer.uid,
      // تاريخ المناسبة (يُبنى منه slot doc id لـ booking_dates)
      eventDate: this.props.bookingDate,
      timeSlot: this.props.timeSlot ?? 'evening',
      guestCount: this.props.guestCount,
      ...(this.props.guestType
        ? {
            guestType: this.props.guestType,
            menGuestCount: this.props.menGuestCount!,
            womenGuestCount: this.props.womenGuestCount!,
          }
        : {}),
      status: this.props.status,
      source: this.props.source ?? 'dashboard',
      ...(this.props.packageInfo ? { packageInfo: this.props.packageInfo } : {}),
      // 📸 لقطات كاملة (تطابق ما يقرؤه تطبيق Flutter)
      ...(this.props.venueSnapshot ? { venue: this.props.venueSnapshot } : {}),
      ...(this.props.packageSnapshot ? { package: this.props.packageSnapshot } : {}),
      searchSnapshot: this.props.searchSnapshot ?? null,
      customerNote: this.props.notes ?? null,
      vendorNotes: this.props.vendorNotes ?? null,
      createdAt: this.props.createdAt.toISOString(),
      updatedAt: this.props.updatedAt.toISOString(),
      //  الحقول الزمنية للحالة (contactedAt / completedExternallyAt /
      //    rejectedAt / bookedElsewhereAt / expiredAt) تكتبها الـ Cloud
      //    Functions المعنية (markContacted / markDateAsBooked /
      //    rejectBookingRequest / expiry) وقت حدوث الانتقال الفعلي.
      cancelledAt: this.props.cancelledAt?.toISOString() ?? null,
    };
  }

  static fromFirestore(id: string, data: Record<string, unknown>): Booking {
    // ─── Customer: 3 schemas ───
    //   1. الجديد: customer: {uid, name, phone, email?, gender?}
    //   2. Flutter app legacy: user: {uid, name, phone, gender}
    //   3. أقدم: userId, userName, userPhone في الجذر
    const customerRaw = (data['customer'] as Record<string, unknown> | undefined) ??
      (data['user'] as Record<string, unknown> | undefined) ??
      {};

    const customer: CustomerInfo = {
      // uid: له قيمة لعملاء التطبيق المسجّلين، null لطلبات الدشبورد
      uid: (customerRaw['uid'] as string) ??
        (data['userId'] as string) ??
        null,
      name: (customerRaw['name'] as string) ??
        (data['customerName'] as string) ??
        (data['userName'] as string) ??
        'عميل',
      phone: (customerRaw['phone'] as string) ??
        (data['customerPhone'] as string) ??
        (data['userPhone'] as string) ??
        '',
    };
    if (customerRaw['email']) customer.email = customerRaw['email'] as string;
    if (customerRaw['gender']) {
      const g = customerRaw['gender'] as string;
      if (g === 'male' || g === 'female' || g === 'other') {
        customer.gender = g;
      }
    }

    // ─── Venue refs: try root-level first, fall back to nested ───
    const venueNested = (data['venue'] as Record<string, unknown> | undefined) ?? {};
    const venueId = (data['venueId'] as string) ??
      (venueNested['venueId'] as string) ??
      '';
    const venueName = (data['venueName'] as string) ??
      (venueNested['name'] as string) ??
      'قاعة';
    // providerId هو مرجع الملكية القياسي.
    const providerId = data['providerId'] as string | undefined;
    if (!providerId) {
      throw new ValidationError('معرّف المزوّد مطلوب', { field: 'providerId' });
    }

    // مراجع العميل (إن وُجدت)
    if (customerRaw['appCustomerId']) {
      customer.appCustomerId = customerRaw['appCustomerId'] as string;
    }
    if (customerRaw['providerCustomerId']) {
      customer.providerCustomerId = customerRaw['providerCustomerId'] as string;
    }

    const props: BookingProps = {
      id,
      venueId,
      venueName,
      providerId,
      ...(typeof data['assignedProviderUserId'] === 'string' &&
      (data['assignedProviderUserId'] as string).trim()
        ? { assignedProviderUserId: (data['assignedProviderUserId'] as string).trim() }
        : {}),
      ...(typeof data['contactedBy'] === 'string' && (data['contactedBy'] as string).trim()
        ? { contactedBy: (data['contactedBy'] as string).trim() }
        : {}),
      customer,
      bookingDate:
        (data['bookingDate'] as string) ??
        (data['eventDate'] as string) ??
        (data['date'] as string) ??
        '',
      timeSlot: (data['timeSlot'] as string) ?? 'evening',
      // 🛡️ guestCount: من الجذر، ثم البحث، ثم الحد الأعلى في لقطة الباقة.
      guestCount: Math.max(
        1,
        (data['guestCount'] as number) ??
          (data['guests'] as number) ??
          ((data['searchSnapshot'] as Record<string, unknown> | undefined)?.[
            'guests'
          ] as number) ??
          ((data['package'] as Record<string, unknown> | undefined)?.[
            'capacityMax'
          ] as number) ??
          ((data['package'] as Record<string, unknown> | undefined)?.[
            'capacity'
          ] as number) ??
          1
      ),
      status: normalizeStatus(data['status']),
      createdAt: parseDate(data['createdAt']),
      updatedAt: parseDate(data['updatedAt']),
    };

    const guestType = data['guestType'];
    const menGuestCount = data['menGuestCount'];
    const womenGuestCount = data['womenGuestCount'];
    if (
      (guestType === 'men' || guestType === 'women' || guestType === 'both') &&
      typeof menGuestCount === 'number' &&
      typeof womenGuestCount === 'number'
    ) {
      props.guestType = guestType;
      props.menGuestCount = menGuestCount;
      props.womenGuestCount = womenGuestCount;
    }

    // ─── displayId (HVL-...) — code جديد، requestId قديم ───
    const code = (data['code'] as string) ?? (data['requestId'] as string);
    if (code) props.displayId = code;

    // ─── packageInfo: try direct, fall back to nested package object ───
    if (data['packageInfo']) {
      const info = data['packageInfo'] as Record<string, unknown>;
      const packageInfo: BookingPackageInfo = {
        key: (info['key'] as string) ?? '',
        ...(typeof info['name'] === 'string' && info['name'].trim()
          ? { name: info['name'].trim() }
          : {}),
        sectionMode: readSectionMode(info['sectionMode']),
        dayType: readDayType(info['dayType']),
        season: readSeason(info['season']),
        hasIncludedServices:
          (info['hasIncludedServices'] as boolean | undefined) ??
          (info['services'] as boolean | undefined) ??
          false,
      };
      if (info['includedServices']) {
        packageInfo.includedServices =
          info['includedServices'] as import('@domain/value-objects/IncludedServices').IncludedServices;
      }
      if (typeof info['includedServicesText'] === 'string') {
        packageInfo.includedServicesText = info['includedServicesText'];
      }
      if (typeof info['includedServicesMenText'] === 'string') {
        packageInfo.includedServicesMenText = info['includedServicesMenText'];
      }
      if (info['includedServicesMen']) {
        packageInfo.includedServicesMen =
          info['includedServicesMen'] as import('@domain/value-objects/IncludedServices').MenIncludedServices;
      }
      if (info['includedServicesWomen']) {
        packageInfo.includedServicesWomen =
          info['includedServicesWomen'] as import('@domain/value-objects/IncludedServices').WomenIncludedServices;
      }
      if (typeof info['includedServicesWomenText'] === 'string') {
        packageInfo.includedServicesWomenText = info['includedServicesWomenText'];
      }
      props.packageInfo = packageInfo;
    } else if (data['package']) {
      const pkg = data['package'] as Record<string, unknown>;
      if (pkg['key'] || pkg['id']) {
        const packageInfo: BookingPackageInfo = {
          key: (pkg['key'] as string) ?? (pkg['id'] as string) ?? '',
          ...(typeof pkg['name'] === 'string' && pkg['name'].trim()
            ? { name: pkg['name'].trim() }
            : {}),
          sectionMode: readSectionMode(pkg['sectionMode']),
          dayType: readDayType(pkg['dayType']),
          season: readSeason(pkg['season']),
          hasIncludedServices:
            (pkg['hasIncludedServices'] as boolean | undefined) ??
            (pkg['services'] as boolean | undefined) ??
            false,
        };
        if (pkg['includedServices']) {
          packageInfo.includedServices =
            pkg['includedServices'] as import('@domain/value-objects/IncludedServices').IncludedServices;
        }
        if (typeof pkg['includedServicesText'] === 'string') {
          packageInfo.includedServicesText = pkg['includedServicesText'];
        }
        if (typeof pkg['includedServicesMenText'] === 'string') {
          packageInfo.includedServicesMenText = pkg['includedServicesMenText'];
        }
        if (pkg['includedServicesMen']) {
          packageInfo.includedServicesMen =
            pkg['includedServicesMen'] as import('@domain/value-objects/IncludedServices').MenIncludedServices;
        }
        if (pkg['includedServicesWomen']) {
          packageInfo.includedServicesWomen =
            pkg['includedServicesWomen'] as import('@domain/value-objects/IncludedServices').WomenIncludedServices;
        }
        if (typeof pkg['includedServicesWomenText'] === 'string') {
          packageInfo.includedServicesWomenText = pkg['includedServicesWomenText'];
        }
        props.packageInfo = packageInfo;
      }
    }

    // ─── snapshots الكاملة (venue/package) — كما هي للتطبيق ───
    if (data['venue'] && typeof data['venue'] === 'object') {
      props.venueSnapshot = data['venue'] as Record<string, unknown>;
    }
    if (data['package'] && typeof data['package'] === 'object') {
      props.packageSnapshot = data['package'] as Record<string, unknown>;
    }

    // ─── notes: try "notes" or "customerNote" ───
    const notes = (data['notes'] as string) ?? (data['customerNote'] as string);
    if (notes) props.notes = notes;
    // 🆕 vendorNotes: نقرأ من vendorNotes أو rejectionReason (Cloud Function تحفظها هنا)
    //    rejectionReason له أولوية لأنه الـ source of truth من الـ reject-booking CF
    const rejectionReasonFromCF = data['rejectionReason'] as string | undefined;
    const vendorNotesField = data['vendorNotes'] as string | undefined;
    if (rejectionReasonFromCF) {
      props.vendorNotes = rejectionReasonFromCF;
    } else if (vendorNotesField) {
      props.vendorNotes = vendorNotesField;
    }

    // ─── source ───
    const source = data['source'] as string | undefined;
    const validSources: BookingSource[] = [
      'app',
      'dashboard',
      'web',
      'seed',
      'havlaa_app',
      'provider_dashboard',
      'admin_dashboard',
      'phone_call',
      'whatsapp',
      'walk_in',
    ];
    if (source && validSources.includes(source as BookingSource)) {
      props.source = source as BookingSource;
    }

    // ─── searchSnapshot ───
    if (data['searchSnapshot']) {
      props.searchSnapshot = data['searchSnapshot'] as BookingSearchSnapshot;
    }

    // 🆕 contactedAt — مع توافق خلفي للحقل القديم confirmedAt
    if (data['contactedAt']) props.contactedAt = parseDate(data['contactedAt']);
    else if (data['confirmedAt']) props.contactedAt = parseDate(data['confirmedAt']);
    if (data['completedExternallyAt']) {
      props.completedExternallyAt = parseDate(data['completedExternallyAt']);
    }
    if (data['rejectedAt']) props.rejectedAt = parseDate(data['rejectedAt']);
    if (data['cancelledAt']) props.cancelledAt = parseDate(data['cancelledAt']);
    if (data['bookedElsewhereAt']) {
      props.bookedElsewhereAt = parseDate(data['bookedElsewhereAt']);
    }
    if (data['expiredAt']) props.expiredAt = parseDate(data['expiredAt']);

    return Booking.create(props);
  }
}

function readSectionMode(value: unknown): SectionModeValue {
  return value === 'men' || value === 'women' || value === 'both'
    ? value
    : 'both';
}

function readDayType(value: unknown): DayTypeValue {
  return value === 'weekday' ||
    value === 'weekend' ||
    value === 'all'
    ? value
    : 'all';
}

function readSeason(value: unknown): SeasonValue {
  return value === 'school' ||
    value === 'summer' ||
    value === 'holiday' ||
    value === 'eid' ||
    value === 'all'
    ? value
    : 'all';
}

/**
 * 🔄 يطبّع قيمة status من Firestore إلى الـ enum الجديد.
 *
 * يتعامل مع أي بيانات قديمة متبقية من نموذج "الحجز المدفوع" السابق:
 *   confirmed → contacted، completed → contacted، refunded → cancelled.
 * (لا يوجد ترحيل بيانات رسمي — مجرد عرض آمن لأي مستندات قديمة.)
 */
function normalizeStatus(raw: unknown): BookingRequestStatusType {
  const s = typeof raw === 'string' ? raw : '';
  switch (s) {
    case BookingRequestStatus.PENDING:
    case BookingRequestStatus.CONTACTED:
    case BookingRequestStatus.COMPLETED_EXTERNALLY:
    case BookingRequestStatus.REJECTED:
    case BookingRequestStatus.CANCELLED:
    case BookingRequestStatus.BOOKED_ELSEWHERE:
    case BookingRequestStatus.EXPIRED:
      return s;
    // legacy mappings (النموذج المدفوع القديم)
    case 'confirmed':
    case 'completed':
      return BookingRequestStatus.CONTACTED;
    case 'refunded':
      return BookingRequestStatus.CANCELLED;
    default:
      return BookingRequestStatus.PENDING;
  }
}

function parseDate(value: unknown): Date {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (typeof value === 'string') return new Date(value);
  // Firestore Timestamp
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
}
